/* eslint-disable @typescript-eslint/no-floating-promises */
import debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';
import { isSSR } from './dom';
import { prefetchBundles } from './latePrefetch';
import { countElementsInViewport } from './isVisible';
import { visualRegressionsMode } from './visualRegressionsMode';

// When the page is just loaded, skeletons will expand only when 15% of them are in the viewport.
export const earlyVisibilityOptions = {
  // keep synced with visible_sections in app/components/performance_metadata/browser.rb
  rootMargin: '-100px',
};

const pageFullyLoaded = () => new Promise<void>((resolve) => {
  const appDiv = document.getElementById('consumer-app') ?? document.getElementById('popmenu-admin-app');
  const isModernBrowser = window.popmenuIsModernBrowser && window.popmenuIsModernBrowser();
  // In visual regression mode lodash.debounce doesn't work because Date.now() is stubbed.
  if (!isModernBrowser || !appDiv || visualRegressionsMode) {
    resolve();
    return;
  }
  let observer: MutationObserver | null = null;
  const onMutation = debounce(() => {
    // `earlyVisibilityOptions` is used by `useLazyScrollObserver`s in the listed components.
    // If they don't come into view within the specified margin, they will not be loaded until scrolled to.
    // `pageFullyLoaded` has to use the same `earlyVisibilityOptions` for these components or
    // otherwise `PerformanceMetadata::Browser` will timeout.
    if (countElementsInViewport('.pm-mslc-loading, .pm-mslc-skeleton, .pm-menu-section-skeleton', earlyVisibilityOptions) > 0 ||
        // Featured menu sections have horizontal slider with content on the right hidden by a circular progress bar.
        // It doesn't change after the page is loaded. So `pageFullyLoaded` should ignore it.
        countElementsInViewport('.MuiCircularProgress-root:not(#section-featuredslug *)') > 0 ||
        countElementsInViewport('.mapboxgl-loading') > 0 ||
        countElementsInViewport('.ecwid-pb-placeholder') > 0) {
      return;
    }
    resolve();
    observer?.disconnect();
  }, 50);
  observer = new MutationObserver(onMutation);
  observer.observe(appDiv, {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });
  onMutation();
});

if (process.env.IS_INSTRUMENTED_BUILD && !isSSR) {
  window.pageFullyLoadedForTests = pageFullyLoaded;
}

export let startTasksPendingHydration : () => void = () => undefined;

const hydrationFinishedPromise = isSSR ? null : new Promise<void>((resolve) => {
  startTasksPendingHydration = resolve;
});

const firstUserInteractionPromise = isSSR ? null : new Promise<void>((resolve) => {
  runAfter('hydrationFinished', async () => {
    await window.popmenuFirstInteraction;
    // Lighthouse doesn't interact with the page, if there is an interaction
    // it means it's not a Lighthouse session. So, after popmenuFirstInteraction
    // we can start executing unimportant tasks without LH regressions.
    // But we still need to wait for all skeletons and loaders to disappear
    // to ensure we don't harm actual user experience.
    await pageFullyLoaded();
    performance.mark('pageFullyLoaded');
    localStorage.setItem('popmenuFirstSessionLoaded', 'true');
    resolve();
  });
});

const firstSessionLoadedPromise = isSSR ? null : new Promise<void>((resolve) => {
  if (localStorage.getItem('popmenuFirstSessionLoaded') !== 'true') {
    firstUserInteractionPromise?.then(() => {
      resolve();
    });
  } else {
    resolve();
  }
});

// Events Timeline for the first session:
// 1. hydrationFinished
// 2. users interacts with the page
// 3. all visible skeletons and loaders disappear
// 4. firstUserInteraction, firstSessionLoaded
//
// Events Timeline for the subsequent sessions:
// 1. firstSessionLoaded
// 2. hydrationFinished
// 3. users interacts with the page
// 4. all visible skeletons and loaders disappear
// 5. firstUserInteraction
type PageLoadStage = 'hydrationFinished' | 'firstUserInteraction' | 'firstSessionLoaded';

// CAUTION: DO NOT TRICK LIGHTHOUSE USING THIS METHOD!!!
// Anything executed after firstUserInteraction and firstSessionLoaded will be ignored by lighthouse.
// Use it ONLY for unimportant tasks.
// For example, when postponing hi-fi images, ensure lo-fi placheolders look good!
export async function runAfter<T>(stage: PageLoadStage, callback?: () => T, options: { blockForTests?: boolean } = {}) {
  if (isSSR) return null;

  if (options.blockForTests && window.popmenuWhenPostponedTasksUnblocked) {
    await window.popmenuWhenPostponedTasksUnblocked;
  }

  switch (stage) {
    case 'hydrationFinished':
      await hydrationFinishedPromise;
      await new Promise((resolve) => { setTimeout(resolve, 0); });
      break;
    // Will wait 6 seconds. If user interacts with the page before 6 seconds, it will start sooner.
    // It will wait for all skeletons to disappear and trigger the callback.
    // Use it for unimportant tasks disturbing lighthouse scores (metrics, data prefetching, etc.)
    case 'firstUserInteraction':
      await firstUserInteractionPromise;
      break;
    // Similar to 'firstUserInteraction', but works only when user visits the page for the first time.
    // Use to fetch data that is not needed in the beginning of the first visit of the page (session query, hi-fi header image etc.)
    //
    // CAUTION:  Be careful! The first impression is important! The page must look good initially.
    case 'firstSessionLoaded':
      await firstSessionLoadedPromise;
      break;
    default: throw new Error(`Unknown stage ${stage}`);
  }

  return callback ? callback() : null;
}

// See comments for runAfter.
export function usePageLoadStageFinished(stage: PageLoadStage) {
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    runAfter(stage, () => {
      setFinished(true);
    });
  }, [stage]);
  return finished;
}

runAfter('firstUserInteraction', prefetchBundles, { blockForTests: true });

runAfter('hydrationFinished', () => {
  if (window.popmenuInitFbq) {
    setTimeout(window.popmenuInitFbq, 0);
  }
  runAfter('firstSessionLoaded', () => {
    if (window.popmenuInjectDeferredScripts) {
      setTimeout(window.popmenuInjectDeferredScripts, 0);
    } else {
      window.shouldInjectDeferredScripts = true;
    }
  });
}, { blockForTests: true });

runAfter('firstSessionLoaded', () => {
  performance.mark('firstSessionLoaded');
});

// CSS animations can't start until the current JavaScript task completes.
// Wrapping a function in |executeWithProgressBar()| will play animation and execute the
// function in the next JS task
export const executeWithProgressBar = (callback: () => void, disableProgressBar = window.popmenuNoProgressBarForButtons) => {
  if (disableProgressBar) {
    callback();
    return;
  }

  window.popmenuShowProgressBar?.();
  setTimeout(() => {
    callback();
    setTimeout(() => {
      window.popmenuHideProgressBar?.();
    }, 10); // Small delay allows covering slow multi-task JS operations.
  }, 0);
};
